import { usePaginatedQuery } from '@hooks/usePaginatedQuery';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { GET_BRANDS_UPERCUT_FILTERED, GET_BRANDS } from '@gql/brand';

export interface BrandsFilter {
    isFeatured?: boolean;
    sector?: string[];
    proId?: string[];
}

/**
 * Enhanced hook to fetch brands based on current user's role
 * Automatically selects the appropriate query (Upercut vs Pro)
 * @param query - Search query string
 * @param page - Current page (0-indexed)
 * @param pageSize - Number of items per page
 * @param filter - Optional filter (isFeatured, sector)
 */
export const useGetBrands = (query: string = '', page: number = 0, pageSize: number = 10, filter: BrandsFilter = {}) => {
    const { user, isUpercut } = useAuth();
    const { isProIdRequired } = useProId();

    const isUpercutView = isUpercut && !isProIdRequired;
    const queryToUse = isUpercutView ? GET_BRANDS_UPERCUT_FILTERED : GET_BRANDS;

    const gqlFilter = Object.keys(filter).length > 0 ? filter : undefined;

    const { rows, totalDocs, loading, isLoadingMore, hasMore, loadMore, error, refetch } = usePaginatedQuery({
        query: queryToUse,
        variables: { query, filter: gqlFilter },
        page,
        pageSize,
        baseLimit: 10,
        paginationMode: 'offset',
        skip: !user,
        extractResult: (data) => {
            const brandsData = isUpercutView ? data?.getBrandsUpercut : data?.getBrands;
            return {
                docs: brandsData?.docs ?? [],
                meta: brandsData?.meta ?? { totalDocs: 0, page: 0, limit: pageSize, totalPages: 0, nextPage: null, prevPage: null },
                totalDocs: brandsData?.meta?.totalDocs ?? 0,
            };
        },
    });

    return {
        brands: rows,
        total: totalDocs,
        loading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refetch,
        isUpercut,
        userRole: user?.role,
    };
};
